import {
  MovieShapeLayerType,
  MovieFillType,
} from 'common/src/modules/movie/movie.enums';
import {IShape, ShapeRect, ShapeEllipse, ShapePath, ShapePolystar} from 'common/src/modules/movie/movieShapes.types';
import applyKeyframe from 'common/src/modules/movie/utils/applyKeyframe';
import {Timestamped} from 'common/src/modules/movie/movieRendering';
import {z} from 'zod';
import {shapeSchema} from '../adBuilderAgent/adBuilderAgent.schemas';

// Type for the shape schema
type ShapeSchemaType = z.infer<typeof shapeSchema>;

/**
 * Helper function to convert RGBA array to hex color
 */
function rgbaArrayToHex(rgba: [number, number, number, number]): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(rgba[0])}${toHex(rgba[1])}${toHex(rgba[2])}`;
}

/**
 * Extracts shape information into schema format at a specific time
 * @param shape - The shape to extract information from
 * @param time - Time in milliseconds to extract the shape state at
 * @returns Shape data in the format expected by shapeSchema
 */
export function extractShapeToSchema(shape: IShape, time = 0): ShapeSchemaType {
  // Apply keyframe to get shape at specific time
  const timedShape: Timestamped<IShape> = applyKeyframe(shape, time);
  
  // Extract transform properties at specific time
  const opacity = timedShape.tr?.a ? timedShape.tr.a * 100 : 100;
  
  // Extract fill properties
  let fillColor: string | undefined;

  if (timedShape.fill && timedShape.fill.t === MovieFillType.Solid && timedShape.fill.o?.c) {
    const rgbaArray = timedShape.fill.o.c as [number, number, number, number];
    fillColor = rgbaArrayToHex(rgbaArray);
  }

  // Extract stroke properties  
  let strokeColor: string | undefined;
  let strokeWidth: number | undefined;

  if (timedShape.stroke && timedShape.stroke.t === MovieFillType.Solid && timedShape.stroke.o?.c) {
    const rgbaArray = timedShape.stroke.o.c as [number, number, number, number];
    strokeColor = rgbaArrayToHex(rgbaArray);
    strokeWidth = timedShape.stroke.w ? timedShape.stroke.w : 2;
  }

  const properties = {
    fillColor,
    strokeColor,
    strokeWidth,
    opacity: Math.round(opacity),
  };

  // Handle different shape types
  switch (timedShape.ty) {
    case MovieShapeLayerType.Rectangle: {
      const rectShape = timedShape as Timestamped<ShapeRect>;

      return {
        type: 'rectangle' as const,
        properties,
        rectangle: {
          width: rectShape.s ? rectShape.s[0] : 100,
          height: rectShape.s ? rectShape.s[1] : 100,
          cornerRadius: rectShape.r ? rectShape.r : 0,
          x: rectShape.p ? rectShape.p[0] : 0,
          y: rectShape.p ? rectShape.p[1] : 0,
        },
      };
    }

    case MovieShapeLayerType.Ellipse: {
      const ellipseShape = timedShape as Timestamped<ShapeEllipse>;

      return {
        type: 'ellipse' as const,
        properties,
        ellipse: {
          width: ellipseShape.s ? ellipseShape.s[0] : 100,
          height: ellipseShape.s ? ellipseShape.s[1] : 100,
        },
      };
    }

    case MovieShapeLayerType.Polystar: {
      const polystarShape = timedShape as Timestamped<ShapePolystar>;
      const isPolygon = polystarShape.type === 0;
      
      return {
        type: 'polygon' as const,
        properties,
        polygon: {
          sides: polystarShape.pts ? polystarShape.pts : 5,
          outerRadius: polystarShape.oRad ? polystarShape.oRad : 50,
          ...(isPolygon ? {} : {
            innerRadius: polystarShape.iRad ? polystarShape.iRad : 25,
          }),
        },
      };
    }

    case MovieShapeLayerType.Path: {
      const pathShape = timedShape as Timestamped<ShapePath>;
      const points = pathShape.path?.val?.v?.map((v, index) => ({
        x: v[0],
        y: v[1],
        ix: pathShape.path.val.i?.[index]?.[0] || 0,
        iy: pathShape.path.val.i?.[index]?.[1] || 0,
        ox: pathShape.path.val.o?.[index]?.[0] || 0,
        oy: pathShape.path.val.o?.[index]?.[1] || 0,
      })) || [];

      return {
        type: 'path' as const,
        properties,
        path: {
          points,
          isClosed: pathShape.path?.c || false,
        },
      };
    }

    default:
      // This should never happen, but we need to handle it for TypeScript
      const _exhaustiveCheck: never = timedShape;

      return _exhaustiveCheck;
  }
} 